// src/components/Batch/BatchManager.jsx
import React, { useEffect, useRef, useState } from "react";
import { useFeedback } from "../../contexts/FeedbackContext";
import { useStorage } from "../../contexts/StorageContext";

const LS_KEY = "mudoBatches";
const FILE_PATH = "batches/mudoBatches.json";

export default function BatchManager({
  onBack,
  onOpenCreateBatch,
  batchVersion,
  onViewBatch,
}) {
  const [batches, setBatches] = useState([]);
  const [products, setProducts] = useState([]);
  const { play, buzz } = useFeedback();

  const { hasFolder, loadJSON, saveJSON, workspaceName } = useStorage();
  const seededRef = useRef(false);

  // ---- helpers ----
  const normalizeStatuses = (list) =>
    (Array.isArray(list) ? list : []).map((b) =>
      b?.status === "Planned" ? { ...b, status: "Not Started" } : b
    );

  const writeLS = (list) => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(list || []));
    } catch {}
  };

  // ---- seed from folder (authoritative) whenever workspace changes ----
  useEffect(() => {
    let alive = true;
    seededRef.current = false;

    (async () => {
      if (!hasFolder) {
        // No folder linked: keep legacy LS as source for now.
        return;
      }

      const disk = await loadJSON(FILE_PATH, null);
      if (!alive) return;

      if (Array.isArray(disk)) {
        const normalized = normalizeStatuses(disk);
        writeLS(normalized); // keep legacy views working
        setBatches(normalized);
      } else {
        // brand-new workspace → clear LS + show empty
        try { localStorage.removeItem(LS_KEY); } catch {}
        setBatches([]);
      }

      seededRef.current = true;
    })();

    return () => {
      alive = false;
    };
  }, [hasFolder, workspaceName, loadJSON]);

  // Rehydrate if files were restored (Receive)
  useEffect(() => {
    const onRestored = async () => {
      if (!hasFolder) return;
      try {
        const disk = await loadJSON(FILE_PATH, []);
        const normalized = normalizeStatuses(disk);
        writeLS(normalized);
        setBatches(normalized);
      } catch {}
    };
    window.addEventListener("mudo:files-restored", onRestored);
    window.addEventListener("mudo:vault-restored", onRestored);
    return () => {
      window.removeEventListener("mudo:files-restored", onRestored);
      window.removeEventListener("mudo:vault-restored", onRestored);
    };
  }, [hasFolder, loadJSON]);

  // ---- initial local boot (first load / legacy mode) ----
  useEffect(() => {
    if (seededRef.current) return; // already seeded from folder this run
    let saved = [];
    try {
      saved = JSON.parse(localStorage.getItem(LS_KEY) || "[]");
    } catch {}
    const normalized = normalizeStatuses(saved);
    if (normalized.length !== saved.length) writeLS(normalized);
    setBatches(normalized);
  }, [batchVersion]);

  // ---- persist LS → folder whenever batches change (mirror) ----
  useEffect(() => {
    if (!hasFolder) return;
    (async () => {
      try {
        await saveJSON(FILE_PATH, Array.isArray(batches) ? batches : []);
      } catch {
        /* no-op */
      }
    })();
  }, [batches, hasFolder, saveJSON]);

  // ---- cross-tab LS updates → mirror into file too ----
  useEffect(() => {
    if (!hasFolder) return;

    const persistFromLS = async () => {
      try {
        const raw = localStorage.getItem(LS_KEY);
        const list = raw ? JSON.parse(raw) : [];
        if (Array.isArray(list)) {
          await saveJSON(FILE_PATH, normalizeStatuses(list));
          setBatches(normalizeStatuses(list));
        } else {
          await saveJSON(FILE_PATH, []);
          setBatches([]);
        }
      } catch {}
    };

    const onStorage = (e) => {
      if (e.key === LS_KEY) persistFromLS();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [hasFolder, saveJSON]);

  // ---- products (legacy LS source for now) ----
  useEffect(() => {
    try {
      const savedProducts = JSON.parse(localStorage.getItem("mudoProducts") || "[]");
      setProducts(Array.isArray(savedProducts) ? savedProducts : []);
    } catch {
      setProducts([]);
    }
  }, [batchVersion]);

  const calculateBatchCost = (batch) => {
    if (!batch?.products?.length) return 0;
    return batch.products.reduce((sum, prodId) => {
      const prod = products.find((p) => String(p.id) === String(prodId));
      return sum + (Number(prod?.finalPrice) || 0);
    }, 0);
  };

  const handleDelete = async (batchId) => {
    play("tap");
    if (!window.confirm("Are you sure you want to delete this batch and all its tasks?")) {
      buzz("light");
      return;
    }

    const updatedBatches = (batches || []).filter((b) => String(b.id) !== String(batchId));
    writeLS(updatedBatches);
    setBatches(updatedBatches);

    // remove legacy tasks tied to this batch
    try {
      const savedTasks = JSON.parse(localStorage.getItem("mudoTasks") || "[]");
      const updatedTasks = (savedTasks || []).filter((t) => String(t.batchId) !== String(batchId));
      localStorage.setItem("mudoTasks", JSON.stringify(updatedTasks));
    } catch {}

    try { if (hasFolder) await saveJSON(FILE_PATH, updatedBatches); } catch {}

    play("stop");
    buzz("medium");
  };

  return (
    <div className="p-6 bg-[var(--color-background)] min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={() => {
            onBack?.();
            play("tap");
            buzz("light");
          }}
          className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
        >
          ← Back
        </button>
        <button
          onClick={() => {
            onOpenCreateBatch?.(null);
            play("start");
            buzz("light");
          }}
          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
        >
          + New Batch
        </button>
      </div>

      <h2 className="text-xl text-[var(--color-primary)] mb-4">Batch Manager</h2>

      {(!batches || batches.length === 0) ? (
        <p className="text-white/50">No batches found. Create one to get started.</p>
      ) : (
        <div className="space-y-4">
          {batches.map((batch) => {
            const cost = calculateBatchCost(batch).toFixed(2);
            const productCount = batch.products?.length || 0;

            return (
              <div
                key={batch.id}
                className="bg-[var(--color-surface)] p-4 rounded-lg flex justify-between items-center"
              >
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    {batch.name || "Unnamed Batch"}
                  </h3>
                  <p className="text-sm text-white/70">
                    Created: {batch.createdDate || "—"} | Products: {productCount}
                  </p>
                  <p className="text-sm text-white/70">
                    Status: {batch.status || "Not Started"} | Total Cost: ${cost}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      onViewBatch?.(batch.id);
                      play("tap");
                    }}
                    className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    View Tasks
                  </button>
                  <button
                    onClick={() => {
                      onOpenCreateBatch?.(batch); // Edit mode
                      play("tap");
                    }}
                    className="px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(batch.id)}
                    className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
